import { Frustum, MathUtils, Points, Vector2, Vector3 } from "three";
import { PointCloud, PointCloudManager } from ".";
import { toast } from "../../../app";
import { t } from "../../../localization";
import { BinaryHeap } from "../binary-heap";
import { readFileJSON } from "../file-system-loader";
import { getProjFactor } from "../utilities";
import { WorkerPool } from "../worker";
import { PointCloudNode } from "./point-cloud";

/** Base class for streamable cloud node */
export class StreamablePointCloudNode extends PointCloudNode {
  public cloud: StreamablePointCloud;
  public parent: StreamablePointCloudNode;
  protected level;

  constructor(name, level, center, size) {
    super();

    this.name = name;
    this.level = level;
    this.size = size;
    this.nodeCenter = center;
  }

  get shift() {
    return this.cloud.shift;
  }

  get workerPool() {
    return this.cloud.workerPool;
  }

  getChildOffset(index) {
    console.warn("Not implemented");
    return {x: 0, y: 0, z: 0};
  }

  addChildNode(node) {
    this.children.push(node);

    // Make sure child nodes are sorted by index
    this.children.sort((a,b) => {
      let indexA = parseInt(a.name.slice(-1));
      let indexB = parseInt(b.name.slice(-1));
      return indexA - indexB;
    });
  }

  getChildNode(index) {
    const childName = `${this.name}${index}`;
    const childSize = this.size / 2.0;
    const childLevel = this.level + 1;

    const {x,y,z} = this.getChildOffset(index);

    const childCenter = new Vector3(
      this.nodeCenter.x + (x - 0.5) * childSize,
      this.nodeCenter.y + (y - 0.5) * childSize,
      this.nodeCenter.z + (z - 0.5) * childSize
    );

    const NodeConstructor = this.constructor as any;
    return new NodeConstructor(this.cloud, this, childName,
      childLevel, childCenter, childSize);
  }

  addToScene() {
    // Create pointcloud object
    let pointsScene = new Points(this.geometry, this.cloud.materialScene);
    let pointsPicker = new Points(this.geometry, this.cloud.materialPicker);

    pointsScene.visible = false;
    pointsPicker.visible = false;

    // Create bounding box object;
    let boundingBox = this.cloud.generateEdgeMesh(this.size);

    this.addCloudData(pointsPicker, pointsScene, boundingBox);
  }
}

/** Base class for streamable cloud */
export class StreamablePointCloud extends PointCloud {
  public nodeCacheSize = 500;
  public spacing = null;
  public workerPool: WorkerPool;
  protected structure = null;
  private _shift;

  constructor(pointclouds: PointCloudManager) {
    super(pointclouds);
  }

  get shift() {
    return this._shift;
  }

  set shift(value) {
    this._shift = value;
  }

  get size() {
    return this.root.size;
  }

  get dataDir() {
    return this.structure.path;
  }

  get fromJSON() {
    return this.structure.fromJSON;
  }

  get metadataPath() {
    console.warn("Not implemented");
    return "";
  }

  async load(structure) {
    this.structure = structure;

    // Add pointcloud and set parameters
    this.pointclouds.addPointCloud(this);
    this.setName(structure.name);
    this.setVisibility(structure.visible);
    this.setIdentifier(structure.identifier);

    try {
      // Read index data
      const metadata = await readFileJSON(this.metadataPath);
      this.decodeMetadata(metadata);
    } catch(err) {
      this.error = true;
      console.error(err);
      toast.error(t("toast.points-load-error", {
        name: structure.name
      }));
    }
  }

  decodeMetadata(data) {
    console.warn("Not implemented");
  }

  /** @private */
  destroyWorkers() {
    this.workerPool.terminateWorkers();
  }

  getVisibleNodes(condition?): PointCloudNode[] {
    let visibleNodes = [];
    if (!this.root) return visibleNodes;

    let stack = [this.root];
    while (stack.length > 0) {
      let node = stack.shift();

      if (!node.visible) continue;
      if (condition && !condition(node)) continue;

      visibleNodes.push(node);
      node.children.forEach(child => {
        stack.push(child);
      });
    }

    return visibleNodes;
  }

  manageNodeCache() {
    if (!this.root) return;

    let stack = [this.root];

    let totalVisibleNodes = 0;
    let unusedNodes = [];

    while (stack.length > 0) {
      let object = stack.shift();

      if (object.visible) {
        totalVisibleNodes++;
      } else if (object.loaded) {
        unusedNodes.push(object);
      } else {
        continue;
      }

      object.children.forEach(child => {
        stack.push(child);
      });
    }

    let totalUnusedNodes = unusedNodes.length;
    let totalNumberNodes = totalVisibleNodes + totalUnusedNodes;
    let maxNodesAllowed = this.nodeCacheSize;

    if (totalNumberNodes < maxNodesAllowed) {
      return;
    }

    // We delete from our list of unused nodes plus an extra percentage of the total
    let numberToDelete = totalNumberNodes - maxNodesAllowed + maxNodesAllowed * 0.20;
    numberToDelete = Math.min(numberToDelete, totalUnusedNodes);
    if (numberToDelete === 0) {
      return;
    }

    // Delete unused nodes, starting at the highest detail level we have
    unusedNodes.reverse();
    unusedNodes.forEach((node, index) => {
      if (index >= numberToDelete) {
        return;
      }

      node.dispose();
    });
  }

  hideDescendants() {
    if (!this.root) return;

    let stack = [this.root];

    while (stack.length > 0) {
      let object = stack.shift();
      if (!object.visible) {
        continue;
      }

      object.setVisibility(false, false);
      object.children.forEach(child => {
        stack.push(child);
      });
    }
  }

  updateVisibility(frustum: Frustum, minPixelPercent, boundsVisible, maxAllowedPoints) {
    if (!this.visible || !this.root) return 0;

    let priorityQueue = new BinaryHeap(x => 1 / x.weight);
    priorityQueue.push({
      node: this.root,
      weight: Number.MAX_VALUE
    });

    let unloadedGeometry = [];
    let numLoadedPoints = 0;
    let loadedToGPUThisFrame = 0;

    let maxNodeDistance = this.materialScene.distance;
    let controls = this.viewer.controls;
    let measurePoint = controls.getCameraTarget();

    while (priorityQueue.size > 0) {
      let element = priorityQueue.pop();
      let node = element.node as StreamablePointCloudNode;

      let newPointsTarget = numLoadedPoints;
      newPointsTarget += node.numPoints;

      // Check to see if the point limit will be exceeded
      if (newPointsTarget >= maxAllowedPoints) {
        continue;
      }

      // Ignore nodes based on our view frustrum
      if (!frustum.intersectsBox(node.boundingBox)) {
        continue;
      }

      // Ignore nodes based on distance slider
      let bounds2d = new Vector2(node.center.x, node.center.y);
      let camera2d = new Vector2(measurePoint.x, measurePoint.y);
      let nodeDistance = bounds2d.distanceTo(camera2d);
      if (nodeDistance > (node.radius + maxNodeDistance)) {
        continue;
      }

      if (node.isGeometryNode && (!node.parent || node.parent.isTreeNode)) {
        if (node.loaded && loadedToGPUThisFrame < this.maxloadedToGPUPerFrame) {
          node.addToScene();
          loadedToGPUThisFrame++;
        } else {
          unloadedGeometry.push(node);
        }
      }

      if (node.isTreeNode) {
        node.setVisibility(true, boundsVisible);
        numLoadedPoints += node.numPoints;
      }

      let cameraPosition = this.viewer.camera.position;
      node.children.forEach(child => {
        const childDistance = child.center.distanceTo(cameraPosition);
        const insideSphere = childDistance < child.radius;

        const screenHeight = this.viewer.height;
        const fov = MathUtils.degToRad(Math.max(this.viewer.camera.fov, 45));
        const projFactor = getProjFactor(fov, childDistance, screenHeight);
        const nodeScreenSize = child.radius * projFactor;

        // Pixel size based filtering
        let minNodeSize = this.viewer.height * minPixelPercent;
        if (nodeScreenSize < minNodeSize) return;

        const weight = insideSphere
          ? Number.MAX_VALUE
          : nodeScreenSize;

        priorityQueue.push({
          node: child,
          weight: weight
        });
      });
    }

    unloadedGeometry = unloadedGeometry.slice(0, this.maxUnloadedNodes);
    unloadedGeometry.forEach(node => {
      node.load();
    });

    return numLoadedPoints;
  }
}
